/**
 * CurvedText Fabric.js extension
 * Source: view/base/web/tool/assets/js/fabric.curvedText.js
 * Browser version — uses DOM div for text height measurement (Node.js version uses measureText)
 */
export function registerCurvedText(fabric: any) {
  if (fabric.CurvedText) return; // already registered

  fabric.CurvedText = fabric.util.createClass(fabric.Object, {
    type:        'curved-text',
    diameter:    250,
    kerning:     0,
    text:        '',
    flipped:     false,
    fill:        '#000',
    fontFamily:  'Times New Roman',
    fontSize:    24,
    fontWeight:  'normal',
    fontStyle:   '',
    strokeStyle: null,
    strokeWidth: 0,

    cacheProperties: fabric.Object.prototype.cacheProperties.concat(
      'diameter','kerning','flipped','fill','fontFamily',
      'fontSize','fontWeight','fontStyle','strokeStyle','strokeWidth'
    ),

    initialize(text: string | { text: string }, options: any) {
      this.text = typeof text === 'object' ? text.text : text;
      this.callSuper('initialize', options || {});
      this.set('lockUniScaling', true);
      const canvas = this.getCircularText();
      this._trimCanvas(canvas);
      this.set('width', canvas.width);
      this.set('height', canvas.height);
    },

    _getFontDeclaration(): string {
      return [this.fontStyle, this.fontWeight, `${this.fontSize}px`, this.fontFamily].join(' ');
    },

    _set(key: string, value: any) {
      if (key === 'scaleX') {
        this.fontSize *= value; this.diameter *= value; this.width *= value;
        this.scaleX = 1; if (this.width < 1) this.width = 1;
      } else if (key === 'scaleY') {
        this.height *= value; this.scaleY = 1; if (this.height < 1) this.height = 1;
      } else { this.callSuper('_set', key, value); }
    },

    _render(ctx: CanvasRenderingContext2D) {
      const canvas = this.getCircularText();
      this._trimCanvas(canvas);
      this.set('width', canvas.width); this.set('height', canvas.height);
      ctx.drawImage(canvas, -this.width / 2, -this.height / 2, this.width, this.height);
      this.setCoords();
    },

    _trimCanvas(canvas: HTMLCanvasElement) {
      const ctx  = canvas.getContext('2d')!;
      const w = canvas.width, h = canvas.height;
      const data = ctx.getImageData(0, 0, w, h).data;
      let minX = w, maxX = 0, minY = h, maxY = 0;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (data[((y * w + x) * 4) + 3] > 0) {
            minX = Math.min(minX, x); maxX = Math.max(maxX, x);
            minY = Math.min(minY, y); maxY = Math.max(maxY, y);
          }
        }
      }
      if (maxX < minX || maxY < minY) return;
      const cut = ctx.getImageData(minX, minY, maxX - minX, maxY - minY);
      canvas.width  = maxX - minX;
      canvas.height = maxY - minY;
      canvas.getContext('2d')!.putImageData(cut, 0, 0);
    },

    getCircularText(): HTMLCanvasElement {
      const text = this.text, diameter = this.diameter, flipped = this.flipped;
      const kerning = this.kerning, fill = this.fill;
      const inwardFacing = !flipped;
      const clockwise    = -1;
      let startAngle     = flipped ? 180 : 0;

      const canvas = fabric.util.createCanvasElement();
      canvas.width = canvas.height = diameter;
      const ctx = canvas.getContext('2d')!;
      ctx.font = this._getFontDeclaration();

      // Browser: measure text height via DOM div
      const d = document.createElement('div');
      d.style.fontFamily = this.fontFamily;
      d.style.fontSize   = this.fontSize + 'px';
      d.style.fontWeight = this.fontWeight;
      d.style.fontStyle  = this.fontStyle;
      d.style.whiteSpace = 'nowrap';
      d.textContent      = text;
      document.body.appendChild(d);
      const textHeight = d.offsetHeight;
      document.body.removeChild(d);

      const chars = inwardFacing ? text.split('').reverse() : text.split('');
      startAngle *= Math.PI / 180;
      ctx.translate(diameter / 2, diameter / 2);
      startAngle += Math.PI * (!inwardFacing ? 1 : 0);
      ctx.textBaseline = 'middle';
      ctx.textAlign    = 'center';

      for (let x = 0; x < chars.length; x++) {
        const cw = ctx.measureText(chars[x]).width;
        startAngle += ((cw + (x === chars.length - 1 ? 0 : kerning)) / (diameter / 2 - textHeight)) / 2 * -clockwise;
      }

      ctx.rotate(startAngle);
      for (let x = 0; x < chars.length; x++) {
        const cw = ctx.measureText(chars[x]).width;
        ctx.rotate((cw / 2) / (diameter / 2 - textHeight) * clockwise);
        if (this.strokeStyle && this.strokeWidth) {
          ctx.strokeStyle = this.strokeStyle; ctx.lineWidth = this.strokeWidth; ctx.miterLimit = 2;
          ctx.strokeText(chars[x], 0, (inwardFacing ? 1 : -1) * (0 - diameter / 2 + textHeight / 2));
        }
        ctx.fillStyle = fill;
        ctx.fillText(chars[x], 0, (inwardFacing ? 1 : -1) * (0 - diameter / 2 + textHeight / 2));
        ctx.rotate((cw / 2 + kerning) / (diameter / 2 - textHeight) * clockwise);
      }
      return canvas;
    },

    toObject(propertiesToInclude: string[]) {
      return this.callSuper('toObject', ['text','diameter','kerning','flipped','fill','fontFamily','fontSize','fontWeight','fontStyle','strokeStyle','strokeWidth'].concat(propertiesToInclude));
    },
  });

  fabric.CurvedText.fromObject = function(object: any, callback: Function) {
    // Fabric's _fromObject signature is (className, object, callback, extraParam) —
    // exactly 4 params. extraParam names a property of `object` to pass as the
    // constructor's first arg: `new klass(object[extraParam], object)`. This used
    // to be called with 5 args ('CurvedText', object, callback, false, 'curved-text')
    // — the stray `false` landed in the extraParam slot (falsy), so Fabric took the
    // `new klass(object)` branch instead: `object` (the whole saved JSON) became the
    // `text` constructor arg and `options` was `undefined`, silently dropping every
    // saved property (left/top/originX/originY/width/height/fontSize/...) back to
    // class defaults on every deserialize — confirmed live: a curved-text object
    // saved at left:200/top:200/originX:center/originY:center reloaded at
    // left:0/top:0/originX:left/originY:top, which is exactly the "renders at the
    // top of the placeholder instead of where it was placed" bug reported against
    // the Preview tab. `extraParam: 'text'` is the correct value here (mirrors
    // fabric.Text.fromObject) so `object.text` becomes the constructor's `text` arg
    // and `object` itself (with all its properties) is correctly passed as `options`.
    return fabric.Object._fromObject('CurvedText', object, callback, 'text');
  };
}
